import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://sarkariadda24x7.com';
export const OFFICIAL_BRAND_NAME = 'Sarkari Adda 24x7';
export const DEFAULT_OG_IMAGE = 'https://sarkariadda24x7.com/images/og-image.jpg';

export function publicOgImage(value) {
  const text = String(value || '').trim().replace(/^["']+|["']+$/g, '');
  return /^https?:\/\//i.test(text) ? text : DEFAULT_OG_IMAGE;
}

/**
 * Standardize brand title
 * If title already contains brand name, preserve it; otherwise append "| Sarkari Adda 24x7"
 */
export const formatPageTitle = (rawTitle) => {
  if (!rawTitle) return `${OFFICIAL_BRAND_NAME} — Government Jobs, Results, Admit Cards & Answer Keys 2026`;
  const cleanTitle = String(rawTitle).trim();
  if (
    cleanTitle.includes('Sarkari Adda 24x7') ||
    cleanTitle.includes('SarkariAdda24x7') ||
    cleanTitle.includes('Sarkari Adda')
  ) {
    return cleanTitle;
  }
  return `${cleanTitle} | ${OFFICIAL_BRAND_NAME}`;
};

/**
 * Generate BreadcrumbList JSON-LD
 * @param {Array<{name: string, url?: string}>} breadcrumbs
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: crumb.name,
      ...(crumb.url ? { item: crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}` } : {})
    }))
  };
};

/**
 * Generate WebSite JSON-LD with brand variants
 */
export const generateWebSiteSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: OFFICIAL_BRAND_NAME,
    alternateName: ['Sarkari Adda 247', 'SarkariAdda24x7', 'Sarkari Adda'],
    url: `${SITE_URL}/`,
    description: "India's dedicated government jobs, exam results, admit cards, answer keys, and scholarship notifications portal.",
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/jobs?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
};

/**
 * Generate Organization JSON-LD
 */
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: OFFICIAL_BRAND_NAME,
    alternateName: ['Sarkari Adda 247', 'SarkariAdda24x7', 'Sarkari Adda'],
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/images/icon-512.png`,
      width: 512,
      height: 512
    },
    description: "Sarkari Adda 24x7 is India's leading authentic government employment news and examination updates aggregation portal.",
    sameAs: [
      'https://whatsapp.com/channel/0029VaAPm9bJJhzW3zTvhN3e',
      'https://t.me/sarkariadda24x7_official',
      'https://twitter.com/SarkariAdda24x7'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@sarkariadda24x7.com',
      contactType: 'customer support',
      availableLanguage: ['en', 'hi']
    }
  };
};

/**
 * Reusable SEO Head component
 */
const SeoHead = ({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  breadcrumbs,
  schema,
  noIndex = false
}) => {
  const pageTitle = formatPageTitle(title);
  const cleanPath = canonicalPath 
    ? (canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`)
    : '';
  const canonicalUrl = `${SITE_URL}${cleanPath}`;
  const metaDescription = description || "Get latest government job notifications 2026, admit cards, exam results, official answer keys and scholarships across India on Sarkari Adda 24x7.";

  const breadcrumbSchema = breadcrumbs ? generateBreadcrumbSchema(breadcrumbs) : null;

  return (
    <Helmet>
      {/* Title & Core Meta */}
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={OFFICIAL_BRAND_NAME} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content={OFFICIAL_BRAND_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={publicOgImage(ogImage)} />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SarkariAdda24x7" />
      <meta name="twitter:creator" content="@SarkariAdda24x7" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage || DEFAULT_OG_IMAGE} />

      {/* BreadcrumbList Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Custom Structured Schemas */}
      {schema && (
        Array.isArray(schema) ? (
          schema.filter(Boolean).map((s, idx) => (
            <script key={idx} type="application/ld+json">
              {JSON.stringify(s)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default SeoHead;
